import { useEffect, useMemo, useState } from "react";
import bots from "@/data/bots";
import {
  trackBotClick,
  trackRequestBuildClick,
  trackEarlyAccessClick,
} from "@/lib/analytics";
import {
  Languages,
  HeartHandshake,
  Sparkles,
  ShieldCheck,
  ArrowUpRight,
  Mail,
  Check,
  Search,
} from "lucide-react";

const REQUEST_BUILD_URL = "https://forms.gle/ehBkc48EvcnU6RSMA";
const EMAIL = "angelaorgill47@gmail.com";

const categories = ["All", ...Array.from(new Set(bots.map((b) => b.category)))];

function Landing() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bots.filter((b) => {
      const matchCat = activeCat === "All" || b.category === activeCat;
      const matchQ = !q || b.name.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, activeCat]);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_title: "Industry Access AI – Landing",
        page_location: window.location.href,
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)] selection:bg-[var(--accent)] selection:text-white">
      {/* NAV */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-md bg-[var(--bg)]/85 border-b border-[var(--line)]"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" data-testid="logo-link">
            <span className="h-8 w-8 rounded-lg bg-[var(--ink)] text-[var(--bg)] grid place-items-center font-display font-bold tracking-tight">
              IA
            </span>
            <span className="font-display text-[15px] tracking-tight">
              Industry Access AI
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[var(--ink-2)]">
            <a href="#assistants" className="hover:text-[var(--ink)] transition-colors">Assistants</a>
            <a href="#why" className="hover:text-[var(--ink)] transition-colors">Why us</a>
            <a href="#testers" className="hover:text-[var(--ink)] transition-colors">Early Access</a>
            <a href="#pricing" className="hover:text-[var(--ink)] transition-colors">Pricing</a>
          </nav>
          <a
            href={REQUEST_BUILD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackRequestBuildClick("nav")}
            data-testid="nav-cta-request-build"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[var(--ink)] text-[var(--bg)] px-4 py-2 text-[13px] font-medium hover:bg-[var(--accent)] transition-colors"
          >
            Request a Build <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-24 h-[480px] w-[480px] rounded-full bg-[var(--accent)]/12 blur-3xl" />
          <div className="absolute top-40 right-0 h-[420px] w-[420px] rounded-full bg-[var(--accent-2)]/10 blur-3xl" />
          <div className="absolute inset-0 grain-overlay opacity-[0.35]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div
              className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-white/60 backdrop-blur px-3 py-1.5 text-xs text-[var(--ink-2)]"
              data-testid="hero-badge"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
              Live now on Google Play · Closed testing open
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-[44px] leading-[1.02] sm:text-[60px] lg:text-[76px] tracking-[-0.035em] text-[var(--ink)]"
            >
              Accessibility‑first AI for{" "}
              <span className="italic text-[var(--accent)]">every industry.</span>
            </h1>

            <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[var(--ink-2)]">
              Industry Access AI gives every industry a multilingual,
              accessibility‑first assistant built to{" "}
              <span className="text-[var(--ink)] font-medium">reduce burnout</span>,{" "}
              <span className="text-[var(--ink)] font-medium">prevent miscommunication</span>, and keep your team productive
              — with emotionally safe tutoring, clear job‑training support, and
              translation across{" "}
              <span className="text-[var(--ink)] font-medium">840+ languages</span>, Tok Pisin, and ICT English.
            </p>

            <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-[var(--ink-2)]">
              These assistants help staff avoid missed sales, orders, meetings,
              classes, deliveries, and deadlines — without replacing jobs. They
              simply make your workers stronger, more confident, and more
              successful.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="#assistants"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] text-[var(--bg)] px-5 py-3 text-sm font-medium hover:bg-[var(--accent)] transition-colors"
              >
                Explore 39 Assistants <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href="#pricing"
                data-testid="hero-pricing-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--ink)]/15 px-5 py-3 text-sm font-medium hover:border-[var(--ink)]/40 transition-colors"
              >
                See pricing
              </a>
              <span className="text-xs text-[var(--ink-3)]">
                3‑Day Free Trial Available
              </span>
            </div>
          </div>

          {/* Stat strip */}
          <div className="lg:col-span-4">
            <div className="grid grid-cols-2 gap-3">
              <Stat kpi="39" label="Industry assistants" />
              <Stat kpi="7" label="High‑risk bots refining" />
              <Stat kpi="840+" label="Languages supported" />
              <Stat kpi="24/7" label="Always available" />
            </div>
            <div className="mt-3 rounded-2xl border border-[var(--line)] bg-white/70 backdrop-blur p-5">
              <div className="flex items-center gap-2 text-[var(--ink)]">
                <ShieldCheck className="w-4 h-4 text-[var(--accent)]" />
                <span className="text-sm font-medium">
                  Trauma‑aware · ND‑friendly
                </span>
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
                The only accessibility‑first, trauma‑aware, neurodivergent‑friendly
                AI ecosystem — live now alongside Accessibility AI on Google Play.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section id="why" className="border-y border-[var(--line)] bg-white/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-8">
          <Value
            icon={<Languages className="w-5 h-5" />}
            title="840+ languages"
            body="Including Tok Pisin and ICT English — built to remove language barriers from real frontline work."
          />
          <Value
            icon={<HeartHandshake className="w-5 h-5" />}
            title="Emotionally safe"
            body="Trauma‑aware, ND‑friendly conversations that match learning style, processing speed, and real‑world capability."
          />
          <Value
            icon={<Sparkles className="w-5 h-5" />}
            title="Built for every industry"
            body="39 industry‑specific assistants — with more high‑risk domains in refinement — or request a custom build."
          />
        </div>
      </section>

      {/* BOT GALLERY */}
      <section id="assistants" className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--ink-3)]">
              The Gallery
            </p>
            <h2 className="font-display mt-3 text-[34px] sm:text-[44px] leading-[1.05] tracking-[-0.03em]">
              Pick your industry assistant and start using tailored, multilingual,
              accessibility‑first AI for your team today.
            </h2>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <label className="relative flex-1 lg:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
              <input
                data-testid="bot-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search assistants…"
                className="w-full rounded-full border border-[var(--line)] bg-white pl-9 pr-4 py-2.5 text-sm placeholder:text-[var(--ink-3)] focus:outline-none focus:border-[var(--ink)] transition-colors"
              />
            </label>
          </div>
        </div>

        {/* Category chips */}
        <div className="mt-7 flex flex-wrap gap-2" data-testid="category-chips">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`chip-${c.toLowerCase()}`}
              onClick={() => setActiveCat(c)}
              className={`text-xs px-3.5 py-1.5 rounded-full border transition-all ${
                activeCat === c
                  ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                  : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink)]/40"
              }`}
            >
              {c}
            </button>
          ))}
          <span className="text-xs text-[var(--ink-3)] self-center ml-1">
            {filtered.length} of {bots.length}
          </span>
        </div>

        {/* Grid */}
        <div
          data-testid="bot-grid"
          className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5"
        >
          {filtered.map((bot) => (
            <BotCard key={bot.url} bot={bot} />
          ))}
          {filtered.length === 0 && (
            <div
              data-testid="empty-results"
              className="col-span-full text-center py-16 text-[var(--ink-3)] text-sm"
            >
              No assistants match that search.
            </div>
          )}
        </div>
      </section>

      {/* EARLY ACCESS */}
      <section
        id="testers"
        className="relative border-y border-[var(--line)] bg-[var(--ink)] text-[var(--bg)] overflow-hidden"
      >
        <div className="absolute inset-0 -z-0 opacity-[0.07] grain-overlay pointer-events-none" />
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[var(--accent)]/20 blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.24em] text-[var(--accent-soft)]">
              Closed Testing · Early Access
            </p>
            <h2 className="font-display mt-3 text-[34px] sm:text-[48px] leading-[1.05] tracking-[-0.03em]">
              Help shape the next generation of multilingual, accessibility‑first
              assistants.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-white/80 max-w-2xl">
              Industry Access AI is currently in closed testing, and I'm inviting
              early users to help shape these tools. If you want to try them
              before public release — and help me reach the required 12 testers —
              you can join the early access program today.
            </p>
            <ul className="mt-6 space-y-2.5 text-[14px] text-white/85">
              {[
                "Full access to all 39 industry assistants",
                "Plus the 7 high‑risk domain bots currently in refinement",
                "Multilingual tutoring & translation across 840+ languages",
                "Emotionally safe, accessibility‑first support",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 mt-0.5 text-[var(--accent-soft)] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white/[0.06] backdrop-blur-md border border-white/10 p-7">
              <div className="flex items-center gap-2 text-[var(--accent-soft)]">
                <Mail className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.2em]">
                  Become a tester
                </span>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-white/85">
                Explore your industry assistant, share feedback, and help bring
                accessibility‑first AI to the world.
              </p>
              <a
                href={`mailto:${EMAIL}?subject=Industry%20Access%20AI%20%E2%80%93%20Early%20Access`}
                onClick={trackEarlyAccessClick}
                data-testid="early-access-email-btn"
                className="mt-5 inline-flex items-center justify-center gap-2 w-full rounded-full bg-[var(--bg)] text-[var(--ink)] px-5 py-3 text-sm font-medium hover:bg-[var(--accent-soft)] hover:text-[var(--ink)] transition-colors"
              >
                Email to Join Closed Testing
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <p className="mt-3 text-[12px] text-white/55 text-center">
                {EMAIL}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ink-3)]">
            Pricing
          </p>
          <h2 className="font-display mt-3 text-[34px] sm:text-[48px] leading-[1.05] tracking-[-0.03em]">
            Choose the build that fits your industry.
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-[var(--ink-2)]">
            Every Industry Access AI build includes multilingual tutoring,
            translation across 840+ languages, and accessibility‑first support.
            Start with a 3‑day free trial — request the build that fits your
            workflow.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-4 py-1.5 text-[12px] uppercase tracking-[0.18em] text-[var(--ink-2)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
            3‑Day Free Trial Available
          </div>
        </div>

        <div
          data-testid="pricing-tiers"
          className="mt-12 grid gap-6 md:grid-cols-3 items-stretch"
        >
          <PricingTier
            testid="tier-industry-access-ai"
            name="Industry Access AI Build"
            price="$59–$99"
            tagline="For teams starting with accessibility, ND‑friendly, and trauma‑aware workflows."
            features={[
              "Emotionally safe conversation design",
              "Multilingual tutoring (840+ languages)",
              "Trauma‑aware, ND‑friendly support",
              "3‑day free trial",
            ]}
            ctaLabel="Request a Build"
            ctaHref={REQUEST_BUILD_URL}
            tierAnalyticsId="industry_access_ai"
          />
          <PricingTier
            testid="tier-standard-industry"
            name="Standard Industry Build"
            price="$129–$199"
            tagline="For growing teams that need a tailored industry assistant end‑to‑end."
            features={[
              "Full industry‑specific assistant",
              "Job‑training + tutoring support",
              "Translation incl. Tok Pisin & ICT English",
              "3‑day free trial",
            ]}
            highlight
            ctaLabel="Request a Build"
            ctaHref={REQUEST_BUILD_URL}
            tierAnalyticsId="standard_industry"
          />
          <PricingTier
            testid="tier-enterprise"
            name="Enterprise Build"
            price="$249–$399+"
            tagline="For organizations that need a fully custom, multi‑workflow rollout."
            features={[
              "Custom workflows for your operations",
              "Priority build & iteration support",
              "Team onboarding + training assets",
              "3‑day free trial",
            ]}
            ctaLabel="Request a Build"
            ctaHref={REQUEST_BUILD_URL}
            tierAnalyticsId="enterprise"
          />
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 text-center">
          <a
            href={REQUEST_BUILD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackRequestBuildClick("pricing_footer", "general")}
            data-testid="request-build-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--ink)] text-[var(--bg)] px-8 py-4 text-[15px] font-medium hover:bg-[var(--accent)] transition-colors"
          >
            Request a Build
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <p className="text-[12px] text-[var(--ink-3)]">
            Opens a short intake form so I can scope your build.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[var(--line)] bg-white/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[var(--ink-2)]">
          <div className="flex items-center gap-2.5">
            <span className="h-7 w-7 rounded-md bg-[var(--ink)] text-[var(--bg)] grid place-items-center font-display font-bold text-[12px]">
              IA
            </span>
            <span>© {new Date().getFullYear()} Industry Access AI</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={`mailto:${EMAIL}`} className="hover:text-[var(--ink)] transition-colors" data-testid="footer-email-link">
              {EMAIL}
            </a>
            <a
              href={REQUEST_BUILD_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackRequestBuildClick("footer")}
              className="hover:text-[var(--ink)] transition-colors"
              data-testid="footer-request-build-link"
            >
              Request a Build
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* --- Subcomponents --- */

function PricingTier({
  testid,
  name,
  price,
  tagline,
  features,
  highlight,
  ctaLabel,
  ctaHref,
  tierAnalyticsId,
}) {
  return (
    <div
      data-testid={testid}
      className={`relative flex flex-col rounded-3xl p-7 sm:p-8 transition-all ${
        highlight
          ? "bg-[var(--ink)] text-[var(--bg)] border border-[var(--ink)] shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)] md:-translate-y-2"
          : "bg-white border border-[var(--line)] hover:border-[var(--ink)]/25"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] text-white px-3 py-1 text-[10px] uppercase tracking-[0.18em]">
          Most popular
        </span>
      )}
      <p
        className={`text-[11px] uppercase tracking-[0.2em] ${
          highlight ? "text-[var(--accent-soft)]" : "text-[var(--ink-3)]"
        }`}
      >
        {name}
      </p>
      <div className="mt-4 flex items-baseline gap-2">
        <span
          className={`font-display text-[38px] sm:text-[44px] leading-none tracking-[-0.03em] ${
            highlight ? "text-[var(--bg)]" : "text-[var(--ink)]"
          }`}
          data-testid={`${testid}-price`}
        >
          {price}
        </span>
      </div>
      <p
        className={`mt-4 text-[14px] leading-relaxed ${
          highlight ? "text-white/80" : "text-[var(--ink-2)]"
        }`}
      >
        {tagline}
      </p>

      <div
        className={`mt-6 h-px w-full ${
          highlight ? "bg-white/15" : "bg-[var(--line)]"
        }`}
      />

      <ul
        className={`mt-6 space-y-2.5 text-[14px] ${
          highlight ? "text-white/90" : "text-[var(--ink-2)]"
        }`}
      >
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                highlight ? "text-[var(--accent-soft)]" : "text-[var(--accent)]"
              }`}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackRequestBuildClick(`tier_${tierAnalyticsId}`, tierAnalyticsId)}
        data-testid={`${testid}-cta`}
        className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[14px] font-medium transition-colors ${
          highlight
            ? "bg-[var(--bg)] text-[var(--ink)] hover:bg-[var(--accent-soft)]"
            : "bg-[var(--ink)] text-[var(--bg)] hover:bg-[var(--accent)]"
        }`}
      >
        {ctaLabel}
        <ArrowUpRight className="w-4 h-4" />
      </a>
    </div>
  );
}

function Stat({ kpi, label }) {
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-white/70 backdrop-blur p-5">
      <div className="font-display text-[32px] leading-none tracking-[-0.03em] text-[var(--ink)]">
        {kpi}
      </div>
      <div className="mt-2 text-[12px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
      </div>
    </div>
  );
}

function Value({ icon, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-[var(--accent)]/10 text-[var(--accent)] grid place-items-center">
        {icon}
      </div>
      <div>
        <h3 className="font-display text-[18px] tracking-tight text-[var(--ink)]">
          {title}
        </h3>
        <p className="mt-1.5 text-[14px] leading-relaxed text-[var(--ink-2)]">
          {body}
        </p>
      </div>
    </div>
  );
}

function BotCard({ bot }) {
  const slug = bot.name.replace(/\s+/g, "-").toLowerCase();
  return (
    <a
      href={bot.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackBotClick(bot.name, bot.url)}
      data-testid={`bot-card-${slug}`}
      className="group relative flex flex-col rounded-2xl border border-[var(--line)] bg-white overflow-hidden transition-all hover:-translate-y-0.5 hover:border-[var(--ink)]/35 hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.25)]"
    >
      <div className="aspect-square w-full overflow-hidden bg-[var(--line)]/40">
        <img
          src={bot.image}
          alt={bot.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>
      <div className="p-3.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)] truncate">
            {bot.category}
          </p>
          <p className="mt-0.5 text-[13.5px] font-medium text-[var(--ink)] truncate">
            {bot.name}
          </p>
        </div>
        <span className="h-7 w-7 flex-shrink-0 rounded-full border border-[var(--line)] grid place-items-center text-[var(--ink-2)] group-hover:bg-[var(--ink)] group-hover:text-[var(--bg)] group-hover:border-[var(--ink)] transition-colors">
          <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </a>
  );
}

export default Landing;
